import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { GenericListComponent } from '../../shared/generic-list/generic-list.component';
import { LoginAttempt } from '../../../models/loginAttempt';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { ColumnDefinition } from '../../../models/columnDefinition';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { LoginAttemptSearchBoxComponent } from '../login-attempt-search-box/login-attempt-search-box.component';
import { LoginAttemptSearchFilter } from '../../../models/loginAttemptSearchFilter';
import { LoginAttemptSearchRequest } from '../../../models/api/loginAttemptSearchRequest';
import { LoginAttemptService } from '../../../services/login-attempt.service';
import { HotToastService } from '@ngxpert/hot-toast';

@Component({
  selector: 'app-login-attempt-list',
  imports: [
    CommonModule,
    GenericListComponent,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    FormsModule,
    NgxSpinnerModule,
    LoginAttemptSearchBoxComponent
  ],
  templateUrl: './login-attempt-list.component.html',
  styleUrls: ['./login-attempt-list.component.scss']
})
export class LoginAttemptListComponent implements OnInit {
  /** Default view window: only attempts from the last N days are shown until the user searches. */
  private static readonly DEFAULT_WINDOW_DAYS = 10;

  loginAttempts: LoginAttempt[] = [];
  totalItems = 0;
  isSearchVisible = false;
  // Start active so the default date-window filter (below) is applied on first load.
  isSearchActive = true;

  // Pagination and sorting state
  currentPage = 1;
  pageSize = 25;
  sortBy = 'attemptDate';
  ascending = false;
  currentSearchFilter: LoginAttemptSearchFilter = {
    username: '',
    ip: '',
    isSuccessful: undefined,
    attemptDateFrom: LoginAttemptListComponent.daysAgo(LoginAttemptListComponent.DEFAULT_WINDOW_DAYS),
    attemptDateTo: undefined
  };

  /** Midnight, `days` days before today. */
  private static daysAgo(days: number): Date {
    const date = new Date();
    date.setDate(date.getDate() - days);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  columnDefinitions: ColumnDefinition[] = [
    { propertyName: 'attemptDate', displayName: 'Attempt Date', dataType: 'datetime' },
    { propertyName: 'username', displayName: 'Username' },
    { propertyName: 'ip', displayName: 'IP Address' },
    { propertyName: 'isSuccessful', displayName: 'Is Successful', dataType: 'boolean' },
    { propertyName: 'message', displayName: 'Message' }
  ];

  constructor(
    private router: Router,
    private spinnerService: NgxSpinnerService,
    private loginAttemptService: LoginAttemptService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadLoginAttempts();
  }

  private loadLoginAttempts(): void {
    if (this.isSearchActive) {
      this.loadLoginAttemptsWithSearch();
    } else {
      this.loadLoginAttemptsWithoutSearch();
    }
  }

  private loadLoginAttemptsWithoutSearch(): void {
    this.spinnerService.show();

    this.loginAttemptService.getLoginAttempts(
      this.currentPage,
      this.pageSize,
      this.sortBy,
      this.ascending
    ).subscribe({
      next: (response) => {
        this.loginAttempts = response.data;
        this.totalItems = response.totalCount;
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading login attempts:', error);
        this.toast.error('Error loading login attempts');
        this.spinnerService.hide();
      }
    });
  }

  private loadLoginAttemptsWithSearch(): void {
    this.spinnerService.show();

    const searchRequest: LoginAttemptSearchRequest = {
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      ascending: this.ascending,
      username: this.currentSearchFilter.username || undefined,
      ip: this.currentSearchFilter.ip || undefined,
      isSuccessful: this.currentSearchFilter.isSuccessful,
      attemptDateFrom: this.currentSearchFilter.attemptDateFrom?.toISOString(),
      attemptDateTo: this.currentSearchFilter.attemptDateTo?.toISOString()
    };

    this.loginAttemptService.searchLoginAttempts(searchRequest).subscribe({
      next: (response) => {
        this.loginAttempts = response.data;
        this.totalItems = response.totalCount;
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error searching login attempts:', error);
        this.toast.error('Error searching login attempts');
        this.spinnerService.hide();
      }
    });
  }

  onItemClick(loginAttempt: LoginAttempt): void {
  }

  onSortChange(sort: Sort): void {
    if (sort.direction) {
      this.sortBy = sort.active;
      this.ascending = sort.direction === 'asc';
      this.loadLoginAttempts();
    }
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadLoginAttempts();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  onSearchFilterChange(searchFilter: LoginAttemptSearchFilter): void {
    this.currentSearchFilter = searchFilter;
    this.currentPage = 1;

    // Check if any search criteria is set
    const hasSearchCriteria =
      (searchFilter.username && searchFilter.username.trim() !== '') ||
      (searchFilter.ip && searchFilter.ip.trim() !== '') ||
      searchFilter.isSuccessful !== undefined ||
      searchFilter.attemptDateFrom !== undefined ||
      searchFilter.attemptDateTo !== undefined;

    this.isSearchActive = hasSearchCriteria;
    this.loadLoginAttempts();
  }
}
